'use client'

import { useCallback, useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { encodeFunctionData } from 'viem'
import { base } from 'viem/chains'

import { useAutoEarnStore } from '~/components/forms/earn/autoEarnStore'
import { prefetchSimulateCalls } from '~/components/txn-display/state-change/utils/simulation'
import { SMART_VAULT_ABI } from '~/constants/abi/SmartVault'
import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useRequireMemo } from '~/hooks/org/useRequireMemo'
import { CreateTransactionData } from '~/hooks/smart-account-actions/createTransactionData.type'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import {
  getAutoEarnModuleCallEnabled,
  usePendingAutoEarnTransaction,
} from '~/hooks/smart-accounts/usePendingAutoEarnTransaction'
import { getPostOnboardingChecklistQueryKey } from '~/hooks/usePostOnboardingChecklist'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import { BasicTransactionType, OrgSmartAccount } from '~/types'
import { sendTrackingData } from '~/utils/tracking'
import {
  getAutoEarnModuleAddress,
  needsEarnMigrationFirst,
  upsertModuleEnabled,
} from '~/utils/vault'

export const useAutoEarnToggle = (
  smartAccount: OrgSmartAccount,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const { passkeyIds: userPasskeyIds } = useUserPasskeys()

  const isSigner = smartAccount.signers.some((signer) =>
    userPasskeyIds.has(signer.id),
  )

  const { createTransaction, isProcessing } = useCreateTransaction()
  const pendingAutoEarnTransaction = usePendingAutoEarnTransaction(
    smartAccount.id,
    { enabled },
  )
  const hasPendingAutoEarnTransaction = !!pendingAutoEarnTransaction

  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()
  const moduleAddress = getAutoEarnModuleAddress()
  const requireMemo = useRequireMemo()

  // Covers the window between submit and the pending query catching up,
  // shared across all hook instances
  const awaitingPendingState = useAutoEarnStore(
    (state) =>
      state.awaitingPendingStates[smartAccount.id] as boolean | undefined,
  )
  const setAwaitingPendingState = useAutoEarnStore(
    (state) => state.setAwaitingPendingState,
  )
  const awaitingPendingDetection = awaitingPendingState !== undefined
  useEffect(() => {
    if (hasPendingAutoEarnTransaction) {
      setAwaitingPendingState(smartAccount.id, undefined)
    }
  }, [hasPendingAutoEarnTransaction, setAwaitingPendingState, smartAccount.id])

  const pendingEnabled = pendingAutoEarnTransaction?.calls
    ?.map((call) =>
      getAutoEarnModuleCallEnabled({
        callTo: call.to,
        smartAccountAddress: pendingAutoEarnTransaction.smartAccountAddress,
        callData: call.data,
      }),
    )
    .find((value) => value !== undefined)

  const handleToggle = useCallback(
    async (enabled: boolean) => {
      sendTrackingData('toggledAutoEarn', {
        enabled,
        smartAccountId: smartAccount.id,
        orgId: activeOrgId,
        smartAccountRole: smartAccount.role,
      })

      const functionName = enabled ? 'enableModule' : 'disableModule'
      const data = encodeFunctionData({
        abi: SMART_VAULT_ABI,
        functionName,
        args: [moduleAddress],
      })
      const calls = [{ to: smartAccount.address, value: 0n, data }]

      const txData: CreateTransactionData = {
        chainId: base.id as SupportedChainId,
        name: `${enabled ? 'Enable' : 'Disable'} Auto Earn`,
        smartAccountId: smartAccount.id,
        smartAccountAddress: smartAccount.address,
        calls,
        isOfframp: false,
        ...(requireMemo && {
          memo: `${enabled ? 'Enabling' : 'Disabling'} auto earn`,
        }),
        types: [BasicTransactionType.CustomTxn],
        closeOnSubmit: true,
        skipModalDelay: true,
      }

      // Before the await so sibling surfaces disable during the request
      setAwaitingPendingState(smartAccount.id, enabled)
      try {
        const transaction = await createTransaction({
          transactionId: undefined,
          txData: {
            ...txData,
            onSuccess: () => {
              prefetchSimulateCalls({
                queryClient,
                smartAccountAddress: smartAccount.address,
                chainId: base.id as SupportedChainId,
                calls,
              })
            },
            onConfirmation: () => {
              // Fast approvals can settle between transaction-feed polls, so
              // the pending-detected effect may never fire — clear here too or
              // the flag latches for the session
              setAwaitingPendingState(smartAccount.id, undefined)
              queryClient.setQueryData<OrgSmartAccount>(
                [QUERY_KEYS.SMART_ACCOUNT, activeOrgId, smartAccount.address],
                (prev) =>
                  prev
                    ? {
                        ...prev,
                        smartAccountModules: upsertModuleEnabled({
                          modules: prev.smartAccountModules ?? [],
                          moduleAddress,
                          enabled,
                        }),
                      }
                    : prev,
              )
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, activeOrgId],
              })
              queryClient.invalidateQueries({
                queryKey: getPostOnboardingChecklistQueryKey(activeOrgId),
              })
            },
          },
          dontOpenDialog: false,
        })
        if (!transaction) setAwaitingPendingState(smartAccount.id, undefined)
      } catch {
        setAwaitingPendingState(smartAccount.id, undefined)
        toast.error('Failed to create Auto Earn transaction')
      }
    },
    [
      smartAccount,
      activeOrgId,
      requireMemo,
      createTransaction,
      queryClient,
      setAwaitingPendingState,
      moduleAddress,
    ],
  )

  // Gated here rather than per surface so every toggle entry point sends
  // 1.0 accounts through the migration instead of leaving 1.0 enabled
  const needsMigrationFirst = needsEarnMigrationFirst(smartAccount)

  const isDisabled =
    needsMigrationFirst ||
    !isSigner ||
    isProcessing ||
    awaitingPendingDetection ||
    hasPendingAutoEarnTransaction

  const disabledReason = needsMigrationFirst
    ? 'Upgrade to Earn 2.0'
    : !isSigner
      ? 'Only account signers can change this'
      : hasPendingAutoEarnTransaction
        ? 'Transaction pending approval'
        : 'Transaction in progress'

  return {
    handleToggle,
    isDisabled,
    disabledReason,
    isProcessing,
    pendingEnabled: pendingEnabled ?? awaitingPendingState,
  }
}
